import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getActionStatuses } from '../api/actionApi'
import { STATUS_KEY } from '../types'

/**
 * Aksiyon durumları (REFERANS: MEETING_ACTION_STATUS) — Durum filtresi + kolon etiketi için dinamik kaynak.
 * Nadiren değişen referans → uzun staleTime. Ad sunucuda dile göre çözülür → aktif dil ({@code i18n.language})
 * key'e girer ki dil değişince durum adları yenilensin.
 */
export function useActionStatuses() {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: ['action-statuses', i18n.language],
    queryFn: getActionStatuses,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Statü kimliği → **görünen ad**, kaynağı DB katalogu (`REFERANS: MEETING_ACTION_STATUS`).
 *
 * <p><b>Neden gerekli (kullanıcı bulgusu 2026-08-27):</b> statü adları bir süre i18n paketinden
 * okunuyordu ve `Aksiyon → Görev` çevirisi sırasında `pending` anahtarı "Görev Bekleniyor"a çevrildi.
 * Katalog ise "Aksiyon Bekleniyor" diyor — ekranda ve toast metinlerinde <b>var olmayan bir statü adı</b>
 * göründü ("görevi önce Görev Bekleniyor durumuna alın" diyen bir uyarı, o adda bir statü yokken).
 * Bu, CLAUDE.md'de kayıtlı olan tuzağın aynısıdır: statü adları DB kataloğuyla birebir eşleşmek
 * zorundadır, çevrilemez.
 *
 * <p>i18n yalnız <b>yedektir</b> ve katalog henüz yüklenmemişken (ilk render) ya da kimlik katalogda
 * yoksa devreye girer — {@link STATUS_KEY}'in kendi dokümanının söylediği rol. Yedek metinler de
 * kataloğun bugünkü değerleriyle aynı tutulur; aksi halde yükleme sırasında ad bir an değişirdi.
 */
export function useActionStatusName() {
  const { t } = useTranslation()
  const statuses = useActionStatuses()
  return (statusId: number | null | undefined): string => {
    if (statusId == null) return ''
    const fromCatalog = statuses.data?.find((x) => x.id === statusId)?.name
    if (fromCatalog != null && fromCatalog !== '') return fromCatalog
    const key = STATUS_KEY[statusId]
    return key ? t(key) : ''
  }
}
